<script setup>
import { onBeforeUnmount, ref, watch } from 'vue'
import { loadYouTubeIframeApi } from '@/composables/useYouTubeIframeApi.js'

// 카드 안에서 "영상만" 보이게 하는 래퍼.
// 유튜브 임베드의 제목 바 / 가운데 재생·일시정지 버튼 / 워터마크 / 자막은 iframe 내부라
// CSS 로 지울 수 없다. 그래서 두 가지를 같이 쓴다.
//   1) 플레이어를 카드보다 OVERSCAN 배 크게 잡고 가운데 정렬 → 위아래 가장자리 UI가 잘려 나간다.
//   2) 재생 중이 아닐 때(로딩·버퍼링·일시정지)는 포스터 이미지로 덮는다 → 스피너와 가운데 버튼이 안 보인다.
// 일시정지 상태로 남지 않도록 PAUSED 를 받으면 바로 다시 재생시키고, 끝나면 처음으로 되감아 반복한다.
//
// "만들기(active)"와 "재생·노출(show)"을 나눠 받는다.
// 플레이어를 띄우는 데만 몇 초가 걸리므로(유튜브 앱 로드 + 핸드셰이크) 앞 섹션에서 미리 만들어 두고,
// 실제 재생과 포스터 걷기는 등장 연출이 끝난 뒤에 한다 — 섹션에 도착하기 전에는 절대 보이지 않는다.
const props = defineProps({
  videoId: { type: String, required: true },
  // 플레이어를 만들어 둘지. 켜도 재생하지 않는다(cue 상태로 대기).
  active: { type: Boolean, default: false },
  // 재생하고 포스터를 걷을지. 등장 연출이 끝난 뒤에 켠다.
  show: { type: Boolean, default: false },
  // 9개가 한꺼번에 뜨면 서로 줄을 선다(브라우저 동시 연결 제한). 순서대로 조금씩 늦춘다.
  order: { type: Number, default: 0 },
})

// 카드 대비 플레이어 배율. 16:9 를 유지한 채 커지므로 위아래가 넉넉히 잘린다.
const OVERSCAN = 1.75
// 플레이어를 만드는 간격.
const CREATE_STAGGER_MS = 150

const host = ref(null)
const playing = ref(false)
let player = null
let createTimer = null
let destroyed = false

const playerVars = {
  autoplay: 0, // 만들어만 두고 기다린다. 재생은 show 가 켜질 때.
  mute: 1,
  controls: 0, // 재생바 숨김
  disablekb: 1, // 키보드 조작 차단(음소거 해제 방지)
  playsinline: 1,
  rel: 0,
  modestbranding: 1,
  iv_load_policy: 3, // 카드/주석 숨김
  cc_load_policy: 0, // 자막 자동 표시 끔
  fs: 0,
  origin: window.location.origin,
}

const create = async () => {
  const YT = await loadYouTubeIframeApi()
  if (destroyed || !host.value || player) return

  player = new YT.Player(host.value, {
    videoId: props.videoId,
    host: 'https://www.youtube-nocookie.com',
    playerVars,
    events: {
      onReady: (e) => {
        // API 가 만든 iframe 은 Vue 밖에 있어 scoped 스타일이 안 먹는다. 직접 채운다.
        const frame = e.target.getIframe()
        frame.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;border:0'
        frame.setAttribute('tabindex', '-1')
        e.target.mute()
        if (props.show) e.target.playVideo()
      },
      onStateChange: (e) => {
        const YTP = window.YT.PlayerState
        if (e.data === YTP.PLAYING) {
          playing.value = true
          return
        }
        playing.value = false
        if (!props.show) return
        // 보여 주는 중에 일시정지/종료로 멈춰 서지 않게 즉시 되살린다.
        if (e.data === YTP.PAUSED) e.target.playVideo()
        if (e.data === YTP.ENDED) {
          e.target.seekTo(0)
          e.target.playVideo()
        }
      },
    },
  })
}

watch(
  () => props.active,
  (on) => {
    clearTimeout(createTimer)
    if (!on || player) return
    createTimer = setTimeout(create, props.order * CREATE_STAGGER_MS)
  },
  { immediate: true },
)

// 만들어 둔 플레이어는 섹션을 벗어나도 버리지 않는다. 다시 내려왔을 때 또 몇 초를 기다리게 된다.
watch(
  () => props.show,
  (on) => {
    if (!player) return
    if (on) player.playVideo?.()
    else {
      player.pauseVideo?.()
      playing.value = false
    }
  },
)

onBeforeUnmount(() => {
  destroyed = true
  clearTimeout(createTimer)
  player?.destroy?.()
  player = null
})
</script>

<template>
  <div class="pointer-events-none absolute inset-0 overflow-hidden">
    <!-- 16:9 를 유지한 채 카드보다 크게. aspect-ratio 로 높이가 따라온다. -->
    <div
      class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 aspect-video"
      :style="{ width: `${OVERSCAN * 100}%` }"
    >
      <div ref="host" class="absolute inset-0"></div>
    </div>

    <!-- 재생 중이 아니거나 아직 보여 줄 때가 아니면 포스터로 덮는다. -->
    <div
      class="absolute inset-0 transition-opacity duration-500"
      :style="{ opacity: show && playing ? 0 : 1 }"
    >
      <slot />
    </div>
  </div>
</template>
